<template>
  <div>
    <secondHeader />
    <div class="cir-main">
      <sidebar />
      <div class="cir-content-wrapper">
        <div class="cir-page-header">
          <h1 class="cir-page-title">まんまるテスト</h1>
          <p class="cir-page-description">
            マウスで円を描いてみよう！<br />
            どれだけ正確な円を描けるかチャレンジ！
            <span class="cir-tip-wrap">
              <span class="cir-tip-trigger">💡 高得点のコツ</span>
              <span class="cir-tip-balloon">
                <strong>判定基準</strong><br />
                ① 形の均一さ（55%）<br />
                - 中心からの距離が均等なほど高得点<br />
                ② 閉じ具合（20%）<br />
                - 始点と終点が近いほど高得点<br />
                ③ 一周カバー率（25%）<br />
                - 360°描ききると高得点
              </span>
            </span>
          </p>
        </div>
        <div class="cir-test-container">
          <div class="cir-test-wrapper">
            <!-- 状態: 準備 -->
            <div v-if="state === 'ready'" class="cir-ready-area">
              <div class="cir-canvas-wrap">
                <canvas
                  ref="canvas"
                  class="cir-canvas"
                  @mousedown="startDrawing"
                  @mousemove="draw"
                  @mouseup="stopDrawing"
                  @mouseleave="stopDrawing"
                  @touchstart.prevent="startDrawingTouch"
                  @touchmove.prevent="drawTouch"
                  @touchend.prevent="stopDrawing"
                ></canvas>
                <div v-if="!hasDrawn" class="cir-canvas-overlay">
                  <p class="cir-canvas-hint">ここに円を描いてね</p>
                  <p class="cir-canvas-subhint">マウスをドラッグして描こう</p>
                </div>
              </div>
              <div class="cir-button-container" v-if="hasDrawn">
                <button @click="judge" class="cir-judge-button">判定する</button>
                <button @click="clearCanvas" class="cir-clear-button">描き直す</button>
              </div>
            </div>

            <!-- 状態: 判定中 -->
            <div v-if="state === 'judging'" class="cir-test-area cir-is-judging">
              <div class="cir-content">
                <h1 class="cir-judging-text">判定中...</h1>
              </div>
            </div>

            <!-- 状態: 結果表示 -->
            <div v-if="state === 'result'" class="cir-result-section">
              <div class="cir-result-card">
                <h2>結果</h2>
                <div class="cir-result-main">
                  <div class="cir-result-left">
                    <div class="cir-score-circle-wrap">
                      <svg class="cir-score-ring" viewBox="0 0 200 200">
                        <circle
                          class="cir-score-ring-bg"
                          cx="100" cy="100" r="85"
                          fill="none" stroke="#e0e4e8" stroke-width="12"
                        />
                        <circle
                          class="cir-score-ring-fill"
                          cx="100" cy="100" r="85"
                          fill="none"
                          :stroke="scoreColor"
                          stroke-width="12"
                          stroke-linecap="round"
                          :stroke-dasharray="ringCircumference"
                          :stroke-dashoffset="ringOffset"
                        />
                      </svg>
                      <div class="cir-score-text-wrap">
                        <span class="cir-score-value">{{ animatedScore }}</span>
                        <span class="cir-score-percent">%</span>
                      </div>
                    </div>
                    <p class="cir-rank" :style="{ color: rank.color }">
                      ランク: {{ rank.name }}
                    </p>
                    <p class="cir-comment">{{ rank.comment }}</p>
                  </div>
                  <div class="cir-result-right">
                    <div class="cir-result-canvas-row">
                      <div class="cir-result-canvas-card">
                        <p class="cir-result-canvas-label">あなたの円</p>
                        <canvas ref="resultUserCanvas" class="cir-result-canvas"></canvas>
                      </div>
                      <div class="cir-result-canvas-card">
                        <p class="cir-result-canvas-label">理想の正円</p>
                        <canvas ref="resultIdealCanvas" class="cir-result-canvas"></canvas>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="state === 'result'" class="cir-button-container">
              <button @click="reset" class="cir-reset-button">もう一度挑戦</button>
              <button @click="share" class="cir-tweet-button">結果をシェアする</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import secondHeader from "../components/secondHeader.vue";
import sidebar from "../components/sidebar.vue";

export default {
  name: "CircleTest",
  components: {
    secondHeader,
    sidebar,
  },
  data() {
    return {
      state: "ready",
      isDrawing: false,
      hasDrawn: false,
      points: [],
      score: 0,
      animatedScore: 0,
      canvasWidth: 800,
      canvasHeight: 560,
      centroid: null,
      avgRadius: 0,
      scoreAnimationId: null,
    };
  },
  computed: {
    ringCircumference() {
      return 2 * Math.PI * 85;
    },
    ringOffset() {
      return this.ringCircumference * (1 - this.animatedScore / 100);
    },
    scoreColor() {
      if (this.score >= 90) return "#2ed573";
      if (this.score >= 70) return "#ffa502";
      if (this.score >= 50) return "#ff6348";
      return "#ff4757";
    },
    rank() {
      if (this.score === 0) return {};

      const ranks = {
        "S+": {
          name: "S+",
          comments: [
            "完璧！コンパスを使ったでしょ！？",
            "神業！真円の達人です！",
            "もはや人間の手とは思えない精度！",
          ],
          color: "#ff4757",
        },
        S: {
          name: "S",
          comments: [
            "素晴らしい！ほぼ完璧な円です！",
            "円のマスター！美しい軌跡です！",
            "手描きとは思えない精度！",
          ],
          color: "#ffa502",
        },
        A: {
          name: "A",
          comments: [
            "かなり上手い！いい感じの円ですね！",
            "あと少しで完璧！惜しい！",
            "器用な手さばきです！",
          ],
          color: "#2ed573",
        },
        B: {
          name: "B",
          comments: [
            "まあまあの出来！もう一息！",
            "なかなかの腕前ですね！",
            "コツは一定のスピードで描くこと！",
          ],
          color: "#1e90ff",
        },
        C: {
          name: "C",
          comments: [
            "ちょっと歪んじゃったかな？",
            "練習あるのみ！頑張って！",
            "ゆっくり丁寧に描いてみよう！",
          ],
          color: "#7f8fa6",
        },
        D: {
          name: "D",
          comments: [
            "それ、円…だよね？",
            "独創的なフォルムですね！",
            "芸術的な丸ですね…！",
          ],
          color: "#a4b0be",
        },
      };

      let resultRank;
      if (this.score >= 95)      resultRank = ranks["S+"];
      else if (this.score >= 85) resultRank = ranks["S"];
      else if (this.score >= 70) resultRank = ranks["A"];
      else if (this.score >= 55) resultRank = ranks["B"];
      else if (this.score >= 35) resultRank = ranks["C"];
      else                       resultRank = ranks["D"];

      const randomIndex = Math.floor(Math.random() * resultRank.comments.length);
      return {
        name: resultRank.name,
        comment: resultRank.comments[randomIndex],
        color: resultRank.color,
      };
    },
  },
  mounted() {
    this.initCanvas();
    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    cancelAnimationFrame(this.scoreAnimationId);
  },
  methods: {
    initCanvas() {
      this.$nextTick(() => {
        const canvas = this.$refs.canvas;
        if (!canvas) return;
        const wrap = canvas.parentElement;
        const maxH = window.innerHeight - 280;
        const w = Math.min(wrap.clientWidth - 6, 900);
        const h = Math.min(w * 0.65, maxH);
        this.canvasWidth = w;
        this.canvasHeight = h;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
      });
    },
    handleResize() {
      if (this.state === "ready") {
        const pts = [...this.points];
        this.initCanvas();
        if (pts.length > 1) {
          this.points = pts;
          this.redrawPoints();
        }
      }
    },
    getCanvasPos(e) {
      const canvas = this.$refs.canvas;
      const rect = canvas.getBoundingClientRect();
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    },
    getTouchPos(e) {
      const canvas = this.$refs.canvas;
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      };
    },
    startDrawing(e) {
      this.isDrawing = true;
      this.points = [];
      this.hasDrawn = false;
      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const pos = this.getCanvasPos(e);
      this.points.push(pos);

      ctx.beginPath();
      ctx.strokeStyle = "#ff6b81";
      ctx.lineWidth = 4;
      ctx.moveTo(pos.x, pos.y);
    },
    startDrawingTouch(e) {
      this.isDrawing = true;
      this.points = [];
      this.hasDrawn = false;
      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const pos = this.getTouchPos(e);
      this.points.push(pos);

      ctx.beginPath();
      ctx.strokeStyle = "#ff6b81";
      ctx.lineWidth = 4;
      ctx.moveTo(pos.x, pos.y);
    },
    draw(e) {
      if (!this.isDrawing) return;
      const pos = this.getCanvasPos(e);
      this.points.push(pos);

      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext("2d");
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    },
    drawTouch(e) {
      if (!this.isDrawing) return;
      const pos = this.getTouchPos(e);
      this.points.push(pos);

      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext("2d");
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    },
    stopDrawing() {
      if (!this.isDrawing) return;
      this.isDrawing = false;
      if (this.points.length > 10) {
        this.hasDrawn = true;
      }
    },
    redrawPoints() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (this.points.length < 2) return;

      ctx.beginPath();
      ctx.strokeStyle = "#ff6b81";
      ctx.lineWidth = 4;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.moveTo(this.points[0].x, this.points[0].y);
      for (let i = 1; i < this.points.length; i++) {
        ctx.lineTo(this.points[i].x, this.points[i].y);
      }
      ctx.stroke();
    },
    clearCanvas() {
      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      this.points = [];
      this.hasDrawn = false;
    },
    calculateScore() {
      const pts = this.points;
      if (pts.length < 20) return 0;

      const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
      const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
      this.centroid = { x: cx, y: cy };

      const radii = pts.map((p) => Math.sqrt((p.x - cx) ** 2 + (p.y - cy) ** 2));
      const meanR = radii.reduce((s, r) => s + r, 0) / radii.length;
      this.avgRadius = meanR;

      if (meanR < 15) return 0;

      const variance = radii.reduce((s, r) => s + (r - meanR) ** 2, 0) / radii.length;
      const stdDev = Math.sqrt(variance);
      const cv = stdDev / meanR;

      const closedDist = Math.sqrt(
        (pts[0].x - pts[pts.length - 1].x) ** 2 +
        (pts[0].y - pts[pts.length - 1].y) ** 2
      );
      const closedRatio = closedDist / meanR;

      const step = Math.max(1, Math.floor(pts.length / 36));
      let sampled = [];
      for (let i = 0; i < pts.length; i += step) {
        sampled.push(pts[i]);
      }
      let totalAngle = 0;
      for (let i = 0; i < sampled.length; i++) {
        const p0 = sampled[i];
        const p1 = sampled[(i + 1) % sampled.length];
        const a0 = Math.atan2(p0.y - cy, p0.x - cx);
        const a1 = Math.atan2(p1.y - cy, p1.x - cx);
        let diff = a1 - a0;
        if (diff > Math.PI) diff -= 2 * Math.PI;
        if (diff < -Math.PI) diff += 2 * Math.PI;
        totalAngle += diff;
      }
      const coverage = Math.min(1, Math.abs(totalAngle) / (2 * Math.PI));

      let uniformityScore = Math.max(0, 1 - cv * 4);
      let closedScore = Math.max(0, 1 - closedRatio * 1.5);
      let coverageScore = coverage;

      let finalScore =
        uniformityScore * 0.55 +
        closedScore * 0.2 +
        coverageScore * 0.25;

      finalScore = Math.round(Math.max(0, Math.min(100, finalScore * 100)));
      return finalScore;
    },
    judge() {
      this.state = "judging";
      this.score = this.calculateScore();
      this.animatedScore = 0;

      setTimeout(() => {
        this.state = "result";
        this.$nextTick(() => {
          this.drawResultCanvases();
          this.animateScore();
        });
      }, 800);
    },
    animateScore() {
      const target = this.score;
      const duration = 1200;
      const startTime = performance.now();

      const tick = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(1, elapsed / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        this.animatedScore = Math.round(eased * target);
        if (progress < 1) {
          this.scoreAnimationId = requestAnimationFrame(tick);
        }
      };
      this.scoreAnimationId = requestAnimationFrame(tick);
    },
    drawResultCanvases() {
      const userCanvas = this.$refs.resultUserCanvas;
      const idealCanvas = this.$refs.resultIdealCanvas;
      if (!userCanvas || !idealCanvas) return;

      const size = 140;
      userCanvas.width = size;
      userCanvas.height = size;
      idealCanvas.width = size;
      idealCanvas.height = size;

      const pts = this.points;
      if (!pts.length) return;

      const minX = Math.min(...pts.map((p) => p.x));
      const maxX = Math.max(...pts.map((p) => p.x));
      const minY = Math.min(...pts.map((p) => p.y));
      const maxY = Math.max(...pts.map((p) => p.y));
      const rangeX = maxX - minX || 1;
      const rangeY = maxY - minY || 1;
      const scale = Math.min((size - 30) / rangeX, (size - 30) / rangeY);
      const offsetX = (size - rangeX * scale) / 2;
      const offsetY = (size - rangeY * scale) / 2;

      const uCtx = userCanvas.getContext("2d");
      uCtx.clearRect(0, 0, size, size);
      uCtx.beginPath();
      uCtx.strokeStyle = "#ff6b81";
      uCtx.lineWidth = 3;
      uCtx.lineCap = "round";
      uCtx.lineJoin = "round";
      const first = pts[0];
      uCtx.moveTo((first.x - minX) * scale + offsetX, (first.y - minY) * scale + offsetY);
      for (let i = 1; i < pts.length; i++) {
        uCtx.lineTo((pts[i].x - minX) * scale + offsetX, (pts[i].y - minY) * scale + offsetY);
      }
      uCtx.stroke();

      const iCtx = idealCanvas.getContext("2d");
      iCtx.clearRect(0, 0, size, size);
      iCtx.beginPath();
      iCtx.strokeStyle = "#2ed573";
      iCtx.lineWidth = 3;
      const r = Math.min(rangeX, rangeY) * scale / 2;
      iCtx.arc(size / 2, size / 2, r, 0, Math.PI * 2);
      iCtx.stroke();
    },
    reset() {
      cancelAnimationFrame(this.scoreAnimationId);
      this.state = "ready";
      this.isDrawing = false;
      this.hasDrawn = false;
      this.points = [];
      this.score = 0;
      this.animatedScore = 0;
      this.centroid = null;
      this.avgRadius = 0;
      this.$nextTick(() => {
        this.initCanvas();
      });
    },
    share() {
      const text = `まんまるテストで【${this.score}%】を記録！ランクは【${this.rank.name}】でした！\nあなたもチャレンジしてみよう！`;
      const url = window.location.href;
      const hashtags = "はいしんツールキット,まんまるテスト";
      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(url)}&hashtags=${encodeURIComponent(hashtags)}`;
      window.open(twitterUrl, "_blank");
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=M+PLUS+Rounded+1c:wght@400;700&display=swap");

.cir-main {
  display: flex;
  flex-direction: row;
  min-height: 100vh;
}

.cir-content-wrapper {
  flex-grow: 1;
  padding: 30px 20px;
  background-color: #f0f4f8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.cir-page-header {
  text-align: center;
  margin-bottom: 20px;
}

.cir-page-title {
  font-size: 2.2rem;
  font-weight: 700;
  color: #3d3d3d;
  margin-bottom: 10px;
}

.cir-page-description {
  font-size: 1.1rem;
  color: #596275;
  line-height: 1.6;
}

.cir-tip-wrap {
  position: relative;
  display: inline-block;
  margin-left: 0.3rem;
}

.cir-tip-trigger {
  font-size: 0.9rem;
  color: #1e90ff;
  cursor: pointer;
  border-bottom: 1px dashed #1e90ff;
  white-space: nowrap;
}

.cir-tip-balloon {
  display: none;
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  background: #3d3d3d;
  color: #fff;
  font-size: 0.85rem;
  line-height: 1.7;
  text-align: left;
  padding: 0.8rem 1rem;
  border-radius: 10px;
  width: 260px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.cir-tip-balloon::after {
  content: "";
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 7px solid transparent;
  border-bottom-color: #3d3d3d;
}

.cir-tip-wrap:hover .cir-tip-balloon {
  display: block;
  animation: cir-tip-fade 0.2s ease-out;
}

@keyframes cir-tip-fade {
  from { opacity: 0; transform: translateX(-50%) translateY(-4px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}

.cir-test-container {
  font-family: "M PLUS Rounded 1c", sans-serif;
  color: #596275;
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
}

.cir-test-wrapper {
  text-align: center;
}

.cir-ready-area {
  background-color: white;
  border-radius: 30px;
  padding: 2rem;
  border: 4px solid #e0e4e8;
}

.cir-canvas-wrap {
  position: relative;
  display: block;
  border-radius: 20px;
  overflow: hidden;
  border: 3px dashed #d1d8e0;
  background: #fafbfc;
  width: 100%;
}

.cir-canvas {
  display: block;
  cursor: crosshair;
  touch-action: none;
}

.cir-canvas-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

.cir-canvas-hint {
  font-size: 1.6rem;
  font-weight: 700;
  color: #a4b0be;
}

.cir-canvas-subhint {
  font-size: 1rem;
  color: #c8d1db;
  margin-top: 0.5rem;
}

.cir-test-area {
  width: 100%;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 30px;
  user-select: none;
  padding: 1rem;
  box-sizing: border-box;
  position: relative;
  border: 4px solid #dcdde1;
  transition: all 0.2s ease;
}

.cir-content {
  text-align: center;
}

.cir-is-judging {
  background-color: #ffa502;
  border-color: #ff7f00;
}

.cir-judging-text {
  font-size: 3rem;
  font-weight: 700;
  color: white;
  animation: cir-pulse 1s infinite;
}

@keyframes cir-pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.08); }
  100% { transform: scale(1); }
}

.cir-result-section {
  width: 100%;
}

.cir-result-card {
  background-color: white;
  padding: 1.5rem 2rem;
  border-radius: 20px;
  max-width: 100%;
  margin: 0 auto;
  border: 4px solid #ffc0cb;
  animation: cir-result-appear 0.5s ease-out;
}

@keyframes cir-result-appear {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.cir-result-card h2 {
  color: #3d3d3d;
  margin-bottom: 0.8rem;
  font-size: 1.4rem;
}

.cir-result-main {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2.5rem;
}

.cir-result-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.cir-result-right {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.cir-score-circle-wrap {
  position: relative;
  width: 160px;
  height: 160px;
  margin: 0 auto 0.8rem;
}

.cir-score-ring {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.cir-score-ring-fill {
  transition: stroke-dashoffset 1.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.cir-score-text-wrap {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: baseline;
}

.cir-score-value {
  font-size: 2.8rem;
  font-weight: 700;
  color: #3d3d3d;
}

.cir-score-percent {
  font-size: 1.2rem;
  font-weight: 700;
  color: #7f8fa6;
  margin-left: 2px;
}

.cir-result-canvas-row {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.cir-result-canvas-card {
  text-align: center;
}

.cir-result-canvas-label {
  font-size: 0.85rem;
  color: #7f8fa6;
  margin-bottom: 0.4rem;
  font-weight: 700;
}

.cir-result-canvas {
  border: 2px solid #e0e4e8;
  border-radius: 12px;
  background: #fafbfc;
}

.cir-rank {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 0.3rem;
}

.cir-comment {
  font-size: 0.95rem;
  color: #596275;
  margin-bottom: 0;
}

.cir-button-container {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.2rem;
}

.cir-judge-button,
.cir-clear-button,
.cir-reset-button,
.cir-tweet-button {
  padding: 1rem 1.5rem;
  border: 0;
  border-radius: 50px;
  color: white;
  font-size: 1.2rem;
  font-weight: 700;
  font-family: "M PLUS Rounded 1c", sans-serif;
  cursor: pointer;
  transition: transform 0.1s ease;
  border-bottom: 4px solid rgba(0, 0, 0, 0.2);
}

.cir-judge-button:active,
.cir-clear-button:active,
.cir-reset-button:active,
.cir-tweet-button:active {
  transform: translateY(2px);
  border-bottom-width: 2px;
}

.cir-judge-button {
  background-color: #2ed573;
}

.cir-clear-button {
  background-color: #7f8fa6;
}

.cir-reset-button {
  background-color: #ff6b81;
}

.cir-tweet-button {
  background-color: #1DA1F2;
}

@media screen and (max-width: 768px) {
  .cir-ready-area {
    padding: 1rem;
  }

  .cir-result-card {
    padding: 1.2rem;
  }

  .cir-result-main {
    flex-direction: column;
    gap: 1rem;
  }

  .cir-score-value {
    font-size: 2.5rem;
  }

  .cir-rank {
    font-size: 1.3rem;
  }

  .cir-comment {
    font-size: 0.9rem;
  }

  .cir-judge-button,
  .cir-clear-button,
  .cir-reset-button,
  .cir-tweet-button {
    font-size: 1rem;
    padding: 0.8rem 1.5rem;
  }

  .cir-button-container {
    flex-direction: column;
    align-items: center;
  }

  .cir-result-canvas-row {
    flex-direction: column;
    align-items: center;
  }
}
</style>
